import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Star, Phone, Globe, MapPin, Clock, Mail, Users, ChevronDown, Briefcase, Building2, User as UserIcon, Tag, X, Award, TrendingUp, Shield, Heart, Linkedin, Facebook, Instagram, Twitter, Youtube } from 'lucide-react';
import Header from './Header';
import Footer from './Footer';
import MobileFooter from './MobileFooter'; // Adjust the path as needed
import baseurl from '../../components/Baseurl/baseurl';
import { Card, Button, Eyebrow, StatusBadge, Badge, Avatar, Loader, EmptyState } from '../ui';

const BusinessListing = () => {
  const [activeTab, setActiveTab] = useState('Overview');
  const [aboutExpanded, setAboutExpanded] = useState(true);
  const [businessProfile, setBusinessProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [ratings, setRatings] = useState([]);
  const [ratingsLoading, setRatingsLoading] = useState(false);
  const [ratingsError, setRatingsError] = useState('');
  const [ratingsReloadKey, setRatingsReloadKey] = useState(0);
  const [showAllReviews, setShowAllReviews] = useState(false);
  const [isReviewPopupOpen, setIsReviewPopupOpen] = useState(false);
  const [selectedReview, setSelectedReview] = useState(null);
  const { id } = useParams();
  const isMounted = useRef(true);
  const currentId = useRef(null);
  const navigate = useNavigate();
  const [categories, setCategories] = useState([]);
  const [categoriesLoading, setCategoriesLoading] = useState(true);
  const [memberDetails, setMemberDetails] = useState(null);
  const [branch, setBranch] = useState([]);
  const [memberDetailsLoading, setMemberDetailsLoading] = useState(false);

  // New state for collapsible sections and popup
  const [expandedSections, setExpandedSections] = useState({
    memberInfo: false,
    businessInfo: false,
    addressContact: false,
    familyInfo: false,
    businessImages: false,
    ratingsReviews: false,
    customerReviews: false
  });
  const [showImagePopup, setShowImagePopup] = useState(false);
  // Popup for gallery media (images/videos)
  const [showMediaPopup, setShowMediaPopup] = useState(false);
  const [mediaPopupData, setMediaPopupData] = useState({ url: '', isVideo: false, alt: '' });
  // Detect hero background brightness to set contrasting title color
  const [isHeroBgLight, setIsHeroBgLight] = useState(true);
  const titleContrastTextClass = isHeroBgLight ? 'text-ink' : 'text-white';

  // Analyze hero image brightness and set title color for contrast
  useEffect(() => {
    let cancelled = false;
    const resolveUrl = () => {
      try {
        const profileImg = businessProfile?.business_profile_image
          ? (businessProfile.business_profile_image.startsWith('https')
            ? businessProfile.business_profile_image
            : `${baseurl}/${businessProfile.business_profile_image}`)
          : '';
        const memberObj = memberDetails || businessProfile?.Member || {};
        const memberImg = memberObj?.profile_image
          ? (memberObj.profile_image.startsWith('https')
            ? memberObj.profile_image
            : `${baseurl}/${memberObj.profile_image}`)
          : '';
        const raw = businessProfile?.media_gallery || '';
        const items = Array.isArray(raw)
          ? raw
          : (typeof raw === 'string' ? raw.split(',').map(s => s && s.trim()).filter(Boolean) : []);
        const firstGallery = items.length > 0
          ? (items[0].startsWith('http') ? items[0] : `${baseurl}/${items[0]}`)
          : '';
        return profileImg || memberImg || firstGallery || '';
      } catch {
        return '';
      }
    };

    const analyze = (url) => {
      try {
        if (!url) {
          if (!cancelled) setIsHeroBgLight(false);
          return;
        }
        const img = new Image();
        img.crossOrigin = 'anonymous';
        img.src = url;
        img.onload = () => {
          try {
            const canvas = document.createElement('canvas');
            const ctx = canvas.getContext('2d', { willReadFrequently: true });
            const width = (canvas.width = 16);
            const height = (canvas.height = 16);
            ctx.drawImage(img, 0, 0, width, height);
            const { data } = ctx.getImageData(0, 0, width, height);
            let r = 0, g = 0, b = 0, count = 0;
            for (let i = 0; i < data.length; i += 4) {
              r += data[i];
              g += data[i + 1];
              b += data[i + 2];
              count++;
            }
            const avgR = r / count;
            const avgG = g / count;
            const avgB = b / count;
            const luminance = 0.2126 * avgR + 0.7152 * avgG + 0.0722 * avgB;
            if (!cancelled) setIsHeroBgLight(luminance > 186);
          } catch (e) {
            if (!cancelled) setIsHeroBgLight(true);
          }
        };
        img.onerror = () => {
          if (!cancelled) setIsHeroBgLight(true);
        };
      } catch (e) {
        if (!cancelled) setIsHeroBgLight(true);
      }
    };

    analyze(resolveUrl());
    return () => { cancelled = true; };
  }, [businessProfile, memberDetails]);

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        setCategoriesLoading(true);
        const response = await fetch(`${baseurl}/api/category/all`, {
          credentials: 'include'
        });

        if (!response.ok) {
          throw new Error(`Failed to fetch categories: ${response.status} ${response.statusText}`);
        }

        const data = await response.json();

        if (isMounted.current && data.success) {
          setCategories(data.data);
        }
      } catch (err) {
        console.error("Error fetching categories:", err);
        if (isMounted.current) {
          setCategories([]);
        }
      } finally {
        if (isMounted.current) {
          setCategoriesLoading(false);
        }
      }
    };

    fetchCategories();
  }, []);

  const getCategoryName = (categoryId) => {
    if (!categoryId || categoriesLoading) return 'N/A';
    // If an ID-like value is provided, try numeric match first
    const numeric = parseInt(categoryId, 10);
    if (!isNaN(numeric)) {
      const byId = categories.find(cat => parseInt(cat.cid, 10) === numeric);
      if (byId) return byId.category_name;
    }
    // Otherwise try name match (case-insensitive)
    const valueStr = String(categoryId).trim().toLowerCase();
    const byName = categories.find(cat => String(cat.category_name).trim().toLowerCase() === valueStr);
    return byName ? byName.category_name : 'Not specified';
  };

  const toggleSection = (section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };
  function convertToBranchObjects(data) {
    const parsed = {};

    // Parse each field safely
    for (const [key, value] of Object.entries(data)) {
      try {
        const arr = JSON.parse(value);
        parsed[key] = Array.isArray(arr) ? arr : [value];
      } catch {
        parsed[key] = [value];
      }
    }

    // Find the maximum array length
    const maxLength = Math.max(...Object.values(parsed).map(v => v.length));

    // Build array of branch objects
    const result = [];
    for (let i = 0; i < maxLength; i++) {
      const obj = {};
      for (const [key, arr] of Object.entries(parsed)) {
        obj[key] = arr[i] !== undefined ? arr[i] : arr[0] ?? null;
      }
      result.push(obj);
    }

    return result;
  }
  function getFirstItem(value) {
    try {
      const arr = JSON.parse(value);
      return Array.isArray(arr) && arr.length > 0 ? arr[0] : null;
    } catch {
      return null; // Return null if JSON is invalid or empty
    }
  }

  useEffect(() => {
    return () => {
      isMounted.current = false;
    };
  }, []);

  useEffect(() => {
    // Skip if id is undefined or if it's the same as the last fetched id
    if (!id || (currentId.current !== null && currentId.current === id)) {
      return;
    }

    // Update the currentId ref
    currentId.current = id;

    const fetchBusinessProfile = async () => {
      try {
        setLoading(true);
        setError(null);
        console.log("Fetching business profile for ID:", id);

        const apiUrl = `${baseurl}/api/business-profile/${id}`;
        console.log("Fetching from URL:", apiUrl);

        const response = await fetch(apiUrl, {
          credentials: 'include'
        });

        console.log("Response status:", response.status);

        if (!response.ok) {
          throw new Error(`Failed to fetch business profile: ${response.status} ${response.statusText}`);
        }

        const data = await response.json();
        console.log("API response data:", data);

        if (isMounted.current) {
          // Fixed: Check for the correct data structure - data.data is a single object, not an array
          if (data.data) {
            const businessdata = {
              company_address: data.data.company_address,
              city: data.data.city,
              state: data.data.state,
              zip_code: data.data.zip_code,
              branch_name: data.data.branch_name,
              email: data.data.email,
              business_work_contact: data.data.business_work_contract,
            }
            setBranch(convertToBranchObjects(businessdata));
            // Check if the ID matches
            if (parseInt(data.data.id) === parseInt(id)) {
              setBusinessProfile({
                ...data.data,
                company_address: getFirstItem(data.data.company_address),
                city: getFirstItem(data.data.city),
                state: getFirstItem(data.data.state),
                zip_code: getFirstItem(data.data.zip_code)
              });
              console.log("Found business profile:", data.data);
              // Fetch full member details to mirror profile page data
              const memberId = data.data.member_id || data.data.Member?.mid;
              if (memberId) {
                try {
                  setMemberDetailsLoading(true);
                  const resp = await fetch(`${baseurl}/api/member/${memberId}`, {
                    headers: {
                      ...(localStorage.getItem('token') ? { 'Authorization': `Bearer ${localStorage.getItem('token')}` } : {})
                    },
                    credentials: 'include'
                  });
                  if (resp.ok) {
                    const json = await resp.json();
                    if (json.success && json.data) {
                      setMemberDetails(json.data);
                    }
                  }
                } catch (e) {
                  console.warn('Could not fetch member details', e);
                } finally {
                  setMemberDetailsLoading(false);
                }
              }
            } else {
              console.error("Business profile ID mismatch");
              setError('Business profile ID mismatch');
            }
          } else {
            console.error("Unexpected API response structure:", data);
            setError('Invalid data format');
          }
        }
      } catch (err) {
        console.error("Error fetching business profile:", err);
        if (isMounted.current) {
          setError(err.message || 'Unable to load business profile');
        }
      } finally {
        if (isMounted.current) {
          setLoading(false);
        }
      }
    };

    fetchBusinessProfile();
  }, [id]);

  // Fetch all ratings and filter by business ID
  useEffect(() => {
    if (!id) return;

    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 10000);

    const fetchRatings = async () => {
      try {
        setRatingsError('');
        setRatingsLoading(true);
        console.log("Fetching all ratings to filter for business ID:", id);

        const ratingsUrl = `${baseurl}/api/ratings/all`;
        console.log("Fetching all ratings from URL:", ratingsUrl);

        const response = await fetch(ratingsUrl, {
          credentials: 'include',
          signal: controller.signal
        });

        console.log("Ratings response status:", response.status);

        if (!response.ok) {
          throw new Error(`Failed to fetch ratings: ${response.status} ${response.statusText}`);
        }

        const ratingsData = await response.json();
        console.log("All ratings API response data:", ratingsData);

        if (isMounted.current) {
          if (ratingsData && ratingsData.data && Array.isArray(ratingsData.data)) {
            // Filter ratings by business ID
            const businessRatings = ratingsData.data.filter(rating =>
              parseInt(rating.business_id) === parseInt(id)
            );
            console.log("Filtered ratings for business:", businessRatings);
            setRatings(businessRatings);
          } else {
            console.error("Unexpected ratings API response structure:", ratingsData);
            setRatings([]);
          }
        }
      } catch (err) {
        console.error("Error fetching ratings:", err);
        if (isMounted.current) {
          setRatings([]);
          setRatingsError(err?.name === 'AbortError' ? 'Request timed out. Please try again.' : (err.message || 'Unable to load reviews'));
        }
      } finally {
        clearTimeout(timeoutId);
        if (isMounted.current) {
          setRatingsLoading(false);
        }
      }
    };

    fetchRatings();

    return () => {
      clearTimeout(timeoutId);
      controller.abort();
    };
  }, [id, ratingsReloadKey]);

  // Calculate average rating
  const averageRating = ratings.length > 0
    ? ratings.reduce((sum, rating) => sum + rating.rating, 0) / ratings.length
    : 0;

  // Calculate rating distribution
  const ratingDistribution = [5, 4, 3, 2, 1].map(rating => {
    const count = ratings.filter(r => r.rating === rating).length;
    const percentage = ratings.length > 0 ? (count / ratings.length) * 100 : 0;
    return { rating, count, percentage };
  });

  const StarRating = ({ rating, size = "w-4 h-4" }) => {
    return (
      <div className="flex">
        {[...Array(5)].map((_, i) => (
          <Star
            key={i}
            className={`${size} ${i < rating ? 'fill-warning text-warning' : 'text-muted/40'}`}
          />
        ))}
      </div>
    );
  };

  // Image Popup Component
  const ImagePopup = ({ imageUrl, altText, onClose }) => {
    if (!imageUrl) return null;

    return (
      <div className="fixed inset-0 bg-navy/80 flex items-center justify-center z-50 p-4" onClick={onClose}>
        <div className="relative max-w-5xl max-h-full">
          <button
            onClick={onClose}
            className="absolute -top-4 -right-4 bg-surface rounded-full p-2 shadow-lift hover:bg-surface2 z-10 transition-all duration-200 hover:scale-110 ring-1 ring-line/15"
          >
            <X className="w-6 h-6 text-ink" />
          </button>
          <img
            src={imageUrl}
            alt={altText}
            className="max-w-full max-h-full object-contain rounded-lg shadow-lift"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      </div>
    );
  };

  // Media Popup (image or video)
  const MediaPopup = ({ url, isVideo, altText, onClose }) => {
    if (!url) return null;
    return (
      <div className="fixed inset-0 bg-navy/80 flex items-center justify-center z-50 p-4" onClick={onClose}>
        <div className="relative max-w-5xl max-h-full w-full" onClick={(e) => e.stopPropagation()}>
          <button
            onClick={onClose}
            className="absolute -top-4 -right-4 bg-surface rounded-full p-2 shadow-lift hover:bg-surface2 z-10 transition-all duration-200 hover:scale-110 ring-1 ring-line/15"
          >
            <X className="w-6 h-6 text-ink" />
          </button>
          <div className="w-full max-h-[80vh] rounded-lg overflow-hidden shadow-lift bg-navy">
            {isVideo ? (
              <video
                src={url}
                controls
                autoPlay
                className="w-full h-full max-h-[80vh] object-contain bg-navy"
              />
            ) : (
              <img
                src={url}
                alt={altText}
                className="w-full h-full max-h-[80vh] object-contain bg-navy"
              />
            )}
          </div>
        </div>
      </div>
    );
  };

  // Review Popup Component
  const ReviewPopup = ({ review, onClose }) => {
    if (!review) return null;

    // Extract reviewer information - adjust based on your API response structure
    const reviewerName = review.ratedBy
      ? `${review.ratedBy.first_name || ''} ${review.ratedBy.last_name || ''}`.trim()
      : review.member_name || 'Anonymous';

    const reviewerInitials = reviewerName !== 'Anonymous'
      ? reviewerName.split(' ').map(n => n.charAt(0)).join('').toUpperCase()
      : 'AN';

    const avatarUrl = review.ratedBy?.profile_image
      ? (review.ratedBy.profile_image.startsWith('https')
        ? review.ratedBy.profile_image
        : `${baseurl}/${review.ratedBy.profile_image}`)
      : review.member_profile_image
        ? (review.member_profile_image.startsWith('https')
          ? review.member_profile_image
          : `${baseurl}/${review.member_profile_image}`)
        : '';

    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-navy/70 p-4" onClick={onClose}>
        <Card padding="md" className="max-w-xl w-full" onClick={(e) => e.stopPropagation()}>
          <div className="flex items-start gap-4">
            <Avatar src={avatarUrl} name={reviewerName !== 'Anonymous' ? reviewerName : reviewerInitials} size="lg" />
            <div className="flex-1 min-w-0">
              <div className="flex items-center justify-between">
                <div className="font-semibold text-ink truncate">{reviewerName}</div>
                <div className="text-sm text-muted">{formatRelativeTime(review.createdAt)}</div>
              </div>
              <div className="mt-1">
                <StarRating rating={review.rating} size="w-4 h-4" />
              </div>
            </div>
          </div>
          <div className="mt-4 text-text whitespace-pre-wrap break-words">
            {review.message || review.review_text || 'No review message provided.'}
          </div>
          <div className="mt-6 text-right">
            <Button variant="ghost" size="sm" onClick={onClose}>Close</Button>
          </div>
        </Card>
      </div>
    );
  };

  // Enhanced Collapsible Card Component
  const CollapsibleCard = ({ title, isExpanded, onToggle, icon: Icon, children, defaultExpanded = false, gradient = false }) => {
    useEffect(() => {
      if (defaultExpanded && !isExpanded) {
        onToggle();
      }
    }, []);

    return (
      <Card padding="none" interactive className="overflow-hidden" style={{ overflowAnchor: 'none' }}>
        <button
          onClick={onToggle}
          className="w-full px-4 sm:px-6 py-4 sm:py-5 flex items-center justify-between transition-all duration-300 hover:bg-primary/5"
        >
          <div className="flex items-center gap-3 sm:gap-4">
            {Icon && (
              <div className="p-2 bg-primary/10 text-primary rounded-md">
                <Icon className="w-5 h-5 sm:w-6 sm:h-6" />
              </div>
            )}
            <h3 className="font-display font-bold text-lg sm:text-xl text-ink">{title}</h3>
          </div>
          <div className={`p-2 rounded-full transition-all duration-300 ${isExpanded ? 'bg-primary/15 rotate-180' : 'bg-line/8'}`}>
            <ChevronDown className="w-4 h-4 sm:w-5 sm:h-5 text-muted" />
          </div>
        </button>
        {isExpanded && (
          <div className="border-t border-line/12" style={{ overflowAnchor: 'none' }}>
            <div className="p-4 sm:p-6 bg-surface/40">
              {children}
            </div>
          </div>
        )}
      </Card>
    );
  };

  // Debugging information
  console.log("Current state:", { loading, error, businessProfile, id, ratings, averageRating });

  if (loading) {
    return (
      <div className="min-h-screen bg-bg flex items-center justify-center">
        <Card padding="lg" className="text-center">
          <Loader label="Loading business profile..." />
          <p className="mt-2 text-sm text-muted">ID: {id || 'Not available'}</p>
        </Card>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-bg flex items-center justify-center px-4">
        <Card padding="lg" className="text-center max-w-md">
          <div className="text-danger text-6xl mb-6">⚠️</div>
          <h2 className="font-display text-2xl font-bold text-ink mb-4">Error Loading Data</h2>
          <p className="text-text mb-6">{error}</p>
          <Button onClick={() => window.location.reload()}>
            Try Again
          </Button>
        </Card>
      </div>
    );
  }

  if (!businessProfile) {
    return (
      <div className="min-h-screen bg-bg flex items-center justify-center px-4">
        <Card padding="lg" className="text-center max-w-md">
          <div className="text-muted text-6xl mb-6">🏢</div>
          <h2 className="font-display text-2xl font-bold text-ink mb-4">Business Profile Not Found</h2>
          <p className="text-text mb-2">The requested business profile could not be found.</p>
          <p className="text-sm text-muted">ID: {id}</p>
        </Card>
      </div>
    );
  }

  // Extract member information from the business profile
  const member = businessProfile.Member;
  const memberName = member ? `${member.first_name || ''} ${member.last_name || ''}`.trim() : 'Unknown Member';
  const memberFamily = member?.MemberFamily || null;
  const resolvedMember = memberDetails || member || {};
  const resolvedFamily = resolvedMember?.MemberFamily || memberFamily || null;

  // Get profile image URL
  const profileImageUrl = businessProfile?.business_profile_image
    ? businessProfile.business_profile_image.startsWith('https')
      ? businessProfile.business_profile_image
      : `${baseurl}/${businessProfile.business_profile_image}`
    : '';

  // Get member profile image URL
  const memberProfileImageUrl = resolvedMember?.profile_image
    ? resolvedMember.profile_image.startsWith('https')
      ? resolvedMember.profile_image
      : `${baseurl}/${resolvedMember.profile_image}`
    : '';

  // Build media gallery URLs (supports comma-separated string or array)
  const rawMedia = businessProfile?.media_gallery || '';
  const mediaItems = Array.isArray(rawMedia)
    ? rawMedia
    : typeof rawMedia === 'string'
      ? rawMedia.split(',').map(s => s && s.trim()).filter(Boolean)
      : [];
  const mediaUrls = mediaItems.map(item => {
    const normalized = item.startsWith('http') ? item : `${baseurl}/${item}`;
    return normalized;
  });
  const heroBackgroundUrl = mediaUrls.length > 0 ? mediaUrls[0] : '';

  // Get social media links
  const socialLinks = {
    website: businessProfile?.website || '',
    facebook: businessProfile?.facebook_link || '',
    instagram: businessProfile?.instagram_link || '',
    linkedin: businessProfile?.linkedin_link || '',
    google: businessProfile?.google_link || ''
  };

  // Resolve hero image URL with multiple fallbacks
  const heroImageUrl = profileImageUrl
    ? profileImageUrl
    : (memberProfileImageUrl
      ? memberProfileImageUrl
      : (heroBackgroundUrl || ''));

  // Helper: Build Google Maps URL for an address string
  const buildMapsUrl = (addressString) => {
    const str = addressString == null ? '' : String(addressString);
    if (!str.trim()) return '#';
    const query = encodeURIComponent(str.trim());
    return `https://www.google.com/maps/search/?api=1&query=${query}`;
  };

  // Helper: Build tel: URL from a phone-like value
  const buildTelHref = (value) => {
    if (!value) return '#';
    const cleaned = String(value).replace(/[^\d+]/g, '');
    return `tel:${cleaned}`;
  };

  // Compose full business address for mapping
  const getBusinessFullAddress = () => {
    const parts = [
      businessProfile?.company_address || '',
      businessProfile?.city || '',
      businessProfile?.state || '',
      businessProfile?.zip_code || ''
    ].filter(Boolean);
    if (parts.length === 0 && businessProfile?.location) {
      const loc = businessProfile.location;
      return typeof loc === 'string' ? loc : (typeof loc === 'object' && loc !== null ? [loc.address, loc.city, loc.state, loc.zip_code].filter(Boolean).join(', ') : '');
    }
    return parts.join(', ');
  };

  // Helper: format children names from JSON or comma separated string
  const formatChildrenNames = (value) => {
    if (!value) return '';
    try {
      if (Array.isArray(value)) {
        return value.filter(Boolean).join(', ');
      }
      if (typeof value === 'string') {
        const trimmed = value.trim();
        if ((trimmed.startsWith('[') && trimmed.endsWith(']')) || trimmed.startsWith('{')) {
          const parsed = JSON.parse(trimmed);
          if (Array.isArray(parsed)) {
            return parsed.filter(Boolean).join(', ');
          }
        }
        return trimmed
          .replace(/^[\[\"]+|[\]\"]+$/g, '')
          .split(',')
          .map((s) => s.replace(/^[\"\s]+|[\"\s]+$/g, ''))
          .filter(Boolean)
          .join(', ');
      }
      return String(value);
    } catch (e) {
      return String(value)
        .replace(/[\[\]"]+/g, '')
        .split(',')
        .map((s) => s.trim())
        .filter(Boolean)
        .join(', ');
    }
  };

  // Helper: relative time for review timestamps
  const formatRelativeTime = (dateValue) => {
    try {
      const date = new Date(dateValue);
      const diffMs = Date.now() - date.getTime();
      const minutes = Math.floor(diffMs / 60000);
      if (minutes < 60) return `${minutes} min${minutes !== 1 ? 's' : ''} ago`;
      const hours = Math.floor(minutes / 60);
      if (hours < 24) return `${hours} hour${hours !== 1 ? 's' : ''} ago`;
      const days = Math.floor(hours / 24);
      if (days < 7) return `${days} day${days !== 1 ? 's' : ''} ago`;
      const weeks = Math.floor(days / 7);
      if (weeks < 4) return `${weeks} week${weeks !== 1 ? 's' : ''} ago`;
      const months = Math.floor(days / 30);
      return `${months} month${months !== 1 ? 's' : ''} ago`;
    } catch {
      return new Date(dateValue).toLocaleDateString();
    }
  };

  return (
    <div className="min-h-screen bg-bg">
      <Header />

      {/* Image Popup */}
      {showImagePopup && (
        <ImagePopup
          imageUrl={memberProfileImageUrl}
          altText={memberName}
          onClose={() => setShowImagePopup(false)}
        />
      )}

      {/* Enhanced Hero Section */}
      <div className="w-full relative overflow-hidden on-dark bg-navy">
        <div className="relative">
          <div className="h-44 lg:h-52 relative">
            {/* Background image */}
            {heroImageUrl && (
              <img
                src={heroImageUrl}
                alt=""
                onError={(e) => { e.currentTarget.style.display = 'none'; }}
                className="absolute inset-0 w-full h-full object-cover z-0"
              />
            )}

            {/* Scrim for legibility */}
            <div className="absolute inset-0 bg-navy/60 z-[5] pointer-events-none"></div>

            <div className="absolute inset-x-0 bottom-0 px-4 sm:px-6 pb-6 sm:pb-8 z-20">
              <div className="max-w-7xl mx-auto">
                <div className="flex flex-col md:flex-row items-center md:items-start space-y-6 md:space-y-0 md:space-x-8">
                  {/* Enhanced Profile Image */}
                  <div className="relative flex-shrink-0">
                    <div className="relative">
                      <div className="w-24 h-24 md:w-28 md:h-28 bg-surface rounded-lg p-1 shadow-lift hover: transition-all duration-300 transform hover:scale-105">
                        <div
                          className="w-full h-full rounded-md overflow-hidden cursor-pointer bg-primary/10 flex items-center justify-center"
                          onClick={() => memberProfileImageUrl && setShowImagePopup(true)}
                        >
                          {memberProfileImageUrl ? (
                            <img
                              src={memberProfileImageUrl}
                              alt={memberName}
                              className="w-full h-full object-cover"
                            />
                          ) : (
                            <UserIcon className="w-12 h-12 text-primary" />
                          )}
                        </div>
                      </div>
                      {/* Status indicator */}
                      {memberProfileImageUrl && (
                        <div className="absolute -bottom-1 -right-1 w-8 h-8 bg-primary rounded-full border-4 border-surface flex items-center justify-center">
                          <Shield className="w-4 h-4 text-onprimary" />
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Enhanced Title Section */}
                  <div className="flex-1 text-center md:text-left">
                    <div className="mb-4">
                      <Eyebrow className="mb-3 justify-center md:justify-start">Business Profile</Eyebrow>
                      <h1 className="mt-2 font-display text-3xl font-bold leading-tight tracking-tight text-white drop-shadow">
                        {businessProfile.company_name}
                      </h1>
                      <div className="flex flex-wrap items-center justify-center md:justify-start gap-3">
                        <span className="px-4 py-2 bg-surface/10 rounded-full text-white font-semibold text-sm border border-white/20">
                          {businessProfile.business_type || 'Business'}
                        </span>
                        <span className="px-4 py-2 bg-primary/25 rounded-full text-white font-medium text-sm border border-primary/40">
                          {memberName}
                        </span>
                        <span className="px-4 py-2 bg-warning/25 rounded-full text-white font-medium text-sm border border-warning/40 flex items-center gap-2">
                          <Star className="w-4 h-4 fill-warning text-warning" />
                          {averageRating.toFixed(1)}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Action Buttons removed as requested */}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Enhanced Contact Info Strip */}
      <div className="bg-surface border-b border-line/12 shadow-card">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4 sm:py-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 sm:gap-6">
            <div className="flex items-center space-x-3 p-3 sm:p-4 bg-surface2 rounded-lg ring-1 ring-line/10">
              <div className="p-2 bg-primary/10 text-primary rounded-md">
                <MapPin className="w-5 h-5" />
              </div>
              <div className="text-left">
                <p className="font-semibold text-ink text-sm sm:text-base">Address</p>
                <a
                  href={buildMapsUrl(getBusinessFullAddress())}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-xs sm:text-sm text-primary hover:text-primary-hi underline-offset-2 hover:underline"
                >
                  {(businessProfile.company_address || businessProfile.location || 'Address not available')}
                  {businessProfile.city && `, ${businessProfile.city}`}
                  {businessProfile.state && `, ${businessProfile.state}`}
                  {businessProfile.zip_code && ` ${businessProfile.zip_code}`}
                </a>
              </div>
            </div>

            <div className="flex items-center space-x-3 p-3 sm:p-4 bg-surface2 rounded-lg ring-1 ring-line/10">
              <div className="p-2 bg-primary/10 text-primary rounded-md">
                <Phone className="w-5 h-5 text-white" />
              </div>
              <div className="text-left">
                <p className="font-semibold text-ink text-sm sm:text-base">Phone</p>
                {member?.contact_no ? (
                  <a
                    href={buildTelHref(member.contact_no)}
                    className="text-xs sm:text-sm text-primary hover:text-primary-hi underline-offset-2 hover:underline"
                  >
                    {member.contact_no}
                  </a>
                ) : (
                  <p className="text-xs sm:text-sm text-muted">Phone not available</p>
                )}
              </div>
            </div>

            <div className="flex items-center space-x-3 p-3 sm:p-4 bg-surface2 rounded-lg ring-1 ring-line/10">
              <div className="p-2 bg-primary/10 text-primary rounded-md">
                <Globe className="w-5 h-5 text-white" />
              </div>
              <div className="text-left">
                <p className="font-semibold text-ink text-sm sm:text-base">Website</p>
                {socialLinks.website ? (
                  <a
                    href={socialLinks.website}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-xs sm:text-sm text-primary hover:text-primary-hi underline-offset-2 hover:underline break-all"
                  >
                    {socialLinks.website}
                  </a>
                ) : (
                  <p className="text-xs sm:text-sm text-muted">Website not available</p>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6 sm:py-8 space-y-6 sm:space-y-8" style={{ overflowAnchor: 'none' }}>

        {/* About This Business Card - hidden for salary type */}
        {((businessProfile?.business_type || '').toLowerCase() !== 'salary') && (
          <CollapsibleCard
            title="About This Business"
            isExpanded={aboutExpanded}
            onToggle={() => setAboutExpanded(prev => !prev)}
            icon={Building2}
            gradient={true}
          >
            <div className="prose max-w-none text-left">
              <p className="text-text text-sm sm:text-base leading-7 sm:leading-8 break-words whitespace-pre-wrap md:max-w-3xl lg:max-w-4xl mx-auto">
                {businessProfile.about || `${businessProfile.company_name} is a ${businessProfile.business_type || 'business'} established in ${businessProfile.business_starting_year || 'recently'}.`}
              </p>
              {businessProfile.tags && (
                <div className="mt-6 flex flex-wrap gap-2">
                  {businessProfile.tags.split(',').map((tag, index) => (
                    <span key={index} className="px-3 py-1.5 sm:px-4 sm:py-2 bg-primary/10 text-primary rounded-lg text-xs sm:text-sm font-medium ring-1 ring-primary/20 hover:bg-primary/15 transition-all duration-200">
                      #{tag.trim()}
                    </span>
                  ))}
                </div>
              )}
            </div>
          </CollapsibleCard>
        )}

        {/* Member Information Card */}
        <CollapsibleCard
          title="Member Information"
          isExpanded={expandedSections.memberInfo}
          onToggle={() => toggleSection('memberInfo')}
          icon={UserIcon}
        >
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 text-sm">
            {/* Basic Information */}
            <div className="space-y-4">
              <h4 className="font-display font-semibold text-primary border-b border-line/12 pb-2 text-left">Basic Information</h4>
              <div className="flex items-center gap-3">
                <UserIcon className="w-4 h-4 text-primary flex-shrink-0" />
                <div className="text-left">
                  <span className="font-semibold text-ink block">Full Name</span>
                  <p className="text-muted">{memberName}</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <Mail className="w-4 h-4 text-primary flex-shrink-0" />
                <div className="text-left">
                  <span className="font-semibold text-ink block">Email Address</span>
                  <p className="text-muted">{resolvedMember?.email || 'Not available'}</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <Phone className="w-4 h-4 text-primary flex-shrink-0" />
                <div className="text-left">
                  <span className="font-semibold text-ink block">Contact Number</span>
                  {resolvedMember?.contact_no ? (
                    <a href={buildTelHref(resolvedMember.contact_no)} className="text-primary hover:text-primary-hi underline-offset-2 hover:underline">{resolvedMember.contact_no}</a>
                  ) : (
                    <p className="text-muted">Not available</p>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-3">
                <Clock className="w-4 h-4 text-primary flex-shrink-0" />
                <div className="text-left">
                  <span className="font-semibold text-ink block">Date of Birth</span>
                  <p className="text-muted">{resolvedMember?.dob || 'Not available'}</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <Users className="w-4 h-4 text-primary flex-shrink-0" />
                <div className="text-left">
                  <span className="font-semibold text-ink block">Gender</span>
                  <p className="text-muted">{resolvedMember?.gender || 'Not available'}</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <Award className="w-4 h-4 text-primary flex-shrink-0" />
                <div className="text-left">
                  <span className="font-semibold text-ink block">Arakattalai</span>
                  <p className="text-muted">{resolvedMember?.Arakattalai || 'No'}</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <TrendingUp className="w-4 h-4 text-primary flex-shrink-0" />
                <div className="text-left">
                  <span className="font-semibold text-ink block">BNI</span>
                  <p className="text-muted">{resolvedMember?.BNI || 'No'}</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <Shield className="w-4 h-4 text-primary flex-shrink-0" />
                <div className="text-left">
                  <span className="font-semibold text-ink block">KBN Member</span>
                  <p className="text-muted">{resolvedMember?.KBN_Member || 'No'}</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <Shield className="w-4 h-4 text-primary flex-shrink-0" />
                <div className="text-left">
                  <span className="font-semibold text-ink block">KNS Member</span>
                  <p className="text-muted">{resolvedMember?.KNS_Member || 'No'}</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <Heart className="w-4 h-4 text-primary flex-shrink-0" />
                <div className="text-left">
                  <span className="font-semibold text-ink block">Lions</span>
                  <p className="text-muted">{resolvedMember?.Lions || 'No'}</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <Globe className="w-4 h-4 text-primary flex-shrink-0" />
                <div className="text-left">
                  <span className="font-semibold text-ink block">Rotary</span>
                  <p className="text-muted">{resolvedMember?.Rotary || 'No'}</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <Users className="w-4 h-4 text-primary flex-shrink-0" />
                <div className="text-left">
                  <span className="font-semibold text-ink block">Other Forum</span>
                  <p className="text-muted">{resolvedMember?.Other_forum || 'No'}</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <Briefcase className="w-4 h-4 text-primary flex-shrink-0" />
                <div className="text-left">
                  <span className="font-semibold text-ink block">Pro</span>
                  <p className="text-muted">{resolvedMember?.pro || 'Unpro'}</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <Heart className="w-4 h-4 text-primary flex-shrink-0" />
                <div className="text-left">
                  <span className="font-semibold text-ink block">Marital Status</span>
                  <p className="text-muted">{resolvedMember?.marital_status || 'Not available'}</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <Shield className="w-4 h-4 text-primary flex-shrink-0" />
                <div className="text-left">
                  <span className="font-semibold text-ink block">Blood Group</span>
                  <p className="text-muted">{resolvedMember?.blood_group || 'Not available'}</p>
                </div>
              </div>
            </div>

            {/* Contact Information */}
            <div className="space-y-4">
              <h4 className="font-display font-semibold text-primary border-b border-line/12 pb-2 text-left">Contact Information</h4>
              <div className="flex items-center gap-3">
                <Phone className="w-4 h-4 text-primary flex-shrink-0" />
                <div className="text-left">
                  <span className="font-semibold text-ink block">Alternative Contact</span>
                  {resolvedMember?.alternate_contact_no ? (
                    <a href={buildTelHref(resolvedMember.alternate_contact_no)} className="text-primary hover:text-primary-hi underline-offset-2 hover:underline">{resolvedMember.alternate_contact_no}</a>
                  ) : (
                    <p className="text-muted">Not available</p>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-3">
                <Mail className="w-4 h-4 text-primary flex-shrink-0" />
                <div className="text-left">
                  <span className="font-semibold text-ink block">Secondary Email</span>
                  <p className="text-muted">{resolvedMember?.secondary_email || 'Not available'}</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <Clock className="w-4 h-4 text-primary flex-shrink-0" />
                <div className="text-left">
                  <span className="font-semibold text-ink block">Best Time to Contact</span>
                  <p className="text-muted">{resolvedMember?.best_time_to_contact || 'Not available'}</p>
                </div>
              </div>
            </div>

            {/* Emergency & Cultural Information */}
            <div className="space-y-4">
              <h4 className="font-display font-semibold text-primary border-b border-line/12 pb-2 text-left">Emergency & Cultural</h4>
              <div className="flex items-center gap-3">
                <Users className="w-4 h-4 text-primary flex-shrink-0" />
                <div className="text-left">
                  <span className="font-semibold text-ink block">Emergency Contact Name</span>
                  <p className="text-muted">{resolvedMember?.emergency_contact || 'Not available'}</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <Phone className="w-4 h-4 text-primary flex-shrink-0" />
                <div className="text-left">
                  <span className="font-semibold text-ink block">Emergency Phone</span>
                  {resolvedMember?.emergency_phone ? (
                    <a href={buildTelHref(resolvedMember.emergency_phone)} className="text-primary hover:text-primary-hi underline-offset-2 hover:underline">{resolvedMember.emergency_phone}</a>
                  ) : (
                    <p className="text-muted">Not available</p>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-3">
                <Shield className="w-4 h-4 text-primary flex-shrink-0" />
                <div className="text-left">
                  <span className="font-semibold text-ink block">Aadhaar Number</span>
                  <p className="text-muted">{resolvedMember?.aadhar_no || 'Not available'}</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <Building2 className="w-4 h-4 text-primary flex-shrink-0" />
                <div className="text-left">
                  <span className="font-semibold text-ink block">Kootam</span>
                  <p className="text-muted">{resolvedMember?.kootam || 'Not available'}</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <Building2 className="w-4 h-4 text-primary flex-shrink-0" />
                <div className="text-left">
                  <span className="font-semibold text-ink block">Kovil</span>
                  <p className="text-muted">{resolvedMember?.kovil || 'Not available'}</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <Award className="w-4 h-4 text-primary flex-shrink-0" />
                <div className="text-left">
                  <span className="font-semibold text-ink block">Access Level</span>
                  <p className="text-muted">
                    <StatusBadge status={resolvedMember?.access_level || 'Basic'} />
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <Shield className="w-4 h-4 text-primary flex-shrink-0" />
                <div className="text-left">
                  <span className="font-semibold text-ink block">Status</span>
                  <p className="text-muted">
                    <StatusBadge status={resolvedMember?.status || 'Unknown'} />
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Address Section */}
          <div className="mt-8 pt-6 border-t border-line/12">
            <h4 className="font-display font-semibold text-primary mb-4 text-left">Address Information</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 text-sm">
              <div className="flex items-center gap-3">
                <MapPin className="w-4 h-4 text-primary flex-shrink-0" />
                <div className="text-left">
                  <span className="font-semibold text-ink block">Street Address</span>
                  {resolvedMember?.address ? (
                    <a
                      href={buildMapsUrl(`${resolvedMember?.address || ''}, ${resolvedMember?.city || ''}, ${resolvedMember?.state || ''} ${resolvedMember?.zip_code || ''}`)}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-primary hover:text-primary-hi underline-offset-2 hover:underline"
                    >
                      {resolvedMember.address}
                    </a>
                  ) : (
                    <p className="text-muted">Not available</p>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-3">
                <MapPin className="w-4 h-4 text-primary flex-shrink-0" />
                <div className="text-left">
                  <span className="font-semibold text-ink block">City</span>
                  {resolvedMember?.city ? (
                    <a
                      href={buildMapsUrl(`${resolvedMember?.city || ''}, ${resolvedMember?.state || ''} ${resolvedMember?.zip_code || ''}`)}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-primary hover:text-primary-hi underline-offset-2 hover:underline"
                    >
                      {resolvedMember.city}
                    </a>
                  ) : (
                    <p className="text-muted">Not available</p>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-3">
                <MapPin className="w-4 h-4 text-primary flex-shrink-0" />
                <div className="text-left">
                  <span className="font-semibold text-ink block">State</span>
                  <p className="text-muted">{resolvedMember?.state || 'Not available'}</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <MapPin className="w-4 h-4 text-primary flex-shrink-0" />
                <div className="text-left">
                  <span className="font-semibold text-ink block">Pin Code</span>
                  <p className="text-muted">{resolvedMember?.zip_code || 'Not available'}</p>
                </div>
              </div>
            </div>
          </div>

          {/* Social Media Section */}
          <div className="mt-8 pt-6 border-t border-line/12">
            <h4 className="font-display font-semibold text-primary mb-4 text-left">Social Media & Online Presence</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 text-sm">
              <div className="flex items-center gap-3">
                <Globe className="w-4 h-4 text-primary flex-shrink-0" />
                <div className="text-left">
                  <span className="font-semibold text-ink block">Personal Website</span>
                  {resolvedMember?.personal_website ? (
                    <a
                      href={resolvedMember.personal_website}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-primary hover:text-primary-hi underline underline-offset-2 break-all"
                    >
                      {resolvedMember.personal_website}
                    </a>
                  ) : (
                    <p className="text-muted">Not available</p>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-3">
                <Linkedin className="w-4 h-4 text-info flex-shrink-0" />
                <div className="text-left">
                  <span className="font-semibold text-ink block">LinkedIn Profile</span>
                  {resolvedMember?.linkedin_profile ? (
                    <a
                      href={resolvedMember.linkedin_profile}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-primary hover:text-primary-hi underline underline-offset-2 break-all"
                    >
                      {resolvedMember.linkedin_profile}
                    </a>
                  ) : (
                    <p className="text-muted">Not available</p>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-3">
                <Facebook className="w-4 h-4 text-info flex-shrink-0" />
                <div className="text-left">
                  <span className="font-semibold text-ink block">Facebook</span>
                  {resolvedMember?.facebook ? (
                    <a
                      href={resolvedMember.facebook}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-primary hover:text-primary-hi underline underline-offset-2 break-all"
                    >
                      {resolvedMember.facebook}
                    </a>
                  ) : (
                    <p className="text-muted">Not available</p>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-3">
                <Instagram className="w-4 h-4 text-accent flex-shrink-0" />
                <div className="text-left">
                  <span className="font-semibold text-ink block">Instagram</span>
                  {resolvedMember?.instagram ? (
                    <a
                      href={resolvedMember.instagram}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-primary hover:text-primary-hi underline underline-offset-2 break-all"
                    >
                      {resolvedMember.instagram}
                    </a>
                  ) : (
                    <p className="text-muted">Not available</p>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-3">
                <Twitter className="w-4 h-4 text-info flex-shrink-0" />
                <div className="text-left">
                  <span className="font-semibold text-ink block">Twitter</span>
                  {resolvedMember?.twitter ? (
                    <a
                      href={resolvedMember.twitter}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-primary hover:text-primary-hi underline underline-offset-2 break-all"
                    >
                      {resolvedMember.twitter}
                    </a>
                  ) : (
                    <p className="text-muted">Not available</p>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-3">
                <Youtube className="w-4 h-4 text-danger flex-shrink-0" />
                <div className="text-left">
                  <span className="font-semibold text-ink block">YouTube</span>
                  {resolvedMember?.youtube ? (
                    <a
                      href={resolvedMember.youtube}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-primary hover:text-primary-hi underline underline-offset-2 break-all"
                    >
                      {resolvedMember.youtube}
                    </a>
                  ) : (
                    <p className="text-muted">Not available</p>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Referral Information (only show if exists) */}
          {resolvedMember?.Referral && (
            <div className="mt-8 pt-6 border-t border-line/12">
              <h4 className="font-display font-semibold text-primary mb-4 text-left">Referral Information</h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 text-sm">
                <div className="flex items-center gap-3">
                  <Users className="w-4 h-4 text-primary flex-shrink-0" />
                  <div className="text-left">
                    <span className="font-semibold text-ink block">Referral Name</span>
                    <p className="text-muted">{resolvedMember.Referral.referral_name || 'Not available'}</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <Tag className="w-4 h-4 text-primary flex-shrink-0" />
                  <div className="text-left">
                    <span className="font-semibold text-ink block">Referral Code</span>
                    <p className="text-muted">{resolvedMember.Referral.referral_code || 'Not available'}</p>
                  </div>
                </div>
              </div>
            </div>
          )}
        </CollapsibleCard>

        {/* Business Information Card */}
        <CollapsibleCard
          title="Business Information"
          isExpanded={expandedSections.businessInfo}
          onToggle={() => toggleSection('businessInfo')}
          icon={Briefcase}
        >
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Left Column */}
            <div className="space-y-6">
              <div className="p-6 bg-surface rounded-lg ring-1 ring-line/10 text-left">
                <h4 className="font-display font-bold text-primary mb-4 flex items-center gap-2">
                  <Building2 className="w-5 h-5 text-primary" />
                  Company Details
                </h4>
                <div className="space-y-4 text-sm text-left">
                  <div className="flex items-start gap-3">
                    <Building2 className="w-4 h-4 text-primary mt-1 flex-shrink-0" />
                    <div>
                      <span className="font-semibold text-ink block mb-1">Company Name</span>
                      <p className="text-text">{businessProfile.company_name || 'Not specified'}</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <Briefcase className="w-4 h-4 text-primary mt-1 flex-shrink-0" />
                    <div>
                      <span className="font-semibold text-ink block mb-1">Business Type</span>
                      <Badge tone="info" className="capitalize">
                        {businessProfile.business_type || 'Not specified'}
                      </Badge>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <Shield className="w-4 h-4 text-primary mt-1 flex-shrink-0" />
                    <div>
                      <span className="font-semibold text-ink block mb-1">Registration Type</span>
                      <p className="text-text">{businessProfile.business_registration_type || 'Not specified'}</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <Award className="w-4 h-4 text-primary mt-1 flex-shrink-0" />
                    <div>
                      <span className="font-semibold text-ink block mb-1">Experience</span>
                      <p className="text-text">{businessProfile.experience || 'Not specified'}</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <Users className="w-4 h-4 text-primary mt-1 flex-shrink-0" />
                    <div>
                      <span className="font-semibold text-ink block mb-1">Staff Size</span>
                      <p className="text-text">{businessProfile.staff_size || 'Not specified'}</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <Tag className="w-4 h-4 text-primary mt-1 flex-shrink-0" />
                    <div>
                      <span className="font-semibold text-ink block mb-1">Category</span>
                      <p className="text-text">{getCategoryName(businessProfile.category_id || businessProfile.category || businessProfile.category_name)}</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* <div className="p-6 bg-surface rounded-2xl border border-primary/15 text-left">
                <h4 className="font-bold text-primary mb-4 flex items-center gap-2">
                  <MapPin className="w-5 h-5 text-primary" />
                  Location & Contact
                </h4>
                <div className="space-y-4 text-sm text-left">
                  <div className="flex items-start gap-3">
                    <Mail className="w-4 h-4 text-primary mt-1 flex-shrink-0" />
                    <div>
                      <span className="font-semibold text-ink block mb-1">Business Email</span>
                      <p className="text-ink">{businessProfile.email || 'Not specified'}</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <Briefcase className="w-4 h-4 text-primary mt-1 flex-shrink-0" />
                    <div>
                      <span className="font-semibold text-ink block mb-1">Work Contract</span>
                      <p className="text-ink">{businessProfile.business_work_contract || 'Not specified'}</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <MapPin className="w-4 h-4 text-primary mt-1 flex-shrink-0" />
                    <div>
                      <span className="font-semibold text-ink block mb-1">Business Address</span>
                      {(businessProfile.company_address || businessProfile.location) ? (
                        <a
                          href={buildMapsUrl(getBusinessFullAddress())}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-primary hover:text-green-800 underline-offset-2 hover:underline"
                        >
                          {businessProfile.company_address || businessProfile.location}
                        </a>
                      ) : (
                        <p className="text-ink">Not specified</p>
                      )}
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <MapPin className="w-4 h-4 text-primary mt-1 flex-shrink-0" />
                    <div>
                      <span className="font-semibold text-ink block mb-1">City</span>
                      <p className="text-ink">{businessProfile.city || 'Not specified'}</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <MapPin className="w-4 h-4 text-primary mt-1 flex-shrink-0" />
                    <div>
                      <span className="font-semibold text-ink block mb-1">State</span>
                      <p className="text-ink">{businessProfile.state || 'Not specified'}</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <Tag className="w-4 h-4 text-primary mt-1 flex-shrink-0" />
                    <div>
                      <span className="font-semibold text-ink block mb-1">Pin Code</span>
                      <p className="text-ink">{businessProfile.zip_code || 'Not specified'}</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <MapPin className="w-4 h-4 text-primary mt-1 flex-shrink-0" />
                    <div>
                      <span className="font-semibold text-ink block mb-1">Location</span>
                      <p className="text-ink">{businessProfile.location || 'Not specified'}</p>
                    </div>
                  </div>
                </div>
              </div> */}

            </div>

            {/* Right Column */}
            <div className="space-y-6">
              <div className="p-6 bg-surface rounded-lg ring-1 ring-line/10 text-left">
                <h4 className="font-display font-bold text-primary mb-4 flex items-center gap-2">
                  <Globe className="w-5 h-5 text-primary" />
                  Online Presence
                </h4>
                <div className="space-y-3 text-sm text-left">
                  {businessProfile.website && (
                    <a href={businessProfile.website} target="_blank" rel="noopener noreferrer"
                      className="flex items-center gap-3 p-3 bg-surface2 rounded-md hover:bg-primary/5 transition-colors group">
                      <Globe className="w-5 h-5 text-accent" />
                      <span className="text-primary underline underline-offset-2 break-all">{businessProfile.website}</span>
                    </a>
                  )}
                  {businessProfile.google_link && (
                    <a href={businessProfile.google_link} target="_blank" rel="noopener noreferrer"
                      className="flex items-center gap-3 p-3 bg-surface2 rounded-md hover:bg-primary/5 transition-colors group">
                      <Globe className="w-5 h-5 text-danger" />
                      <span className="text-primary underline underline-offset-2 break-all">{businessProfile.google_link}</span>
                    </a>
                  )}
                  {businessProfile.facebook_link && (
                    <a href={businessProfile.facebook_link} target="_blank" rel="noopener noreferrer"
                      className="flex items-center gap-3 p-3 bg-surface2 rounded-md hover:bg-primary/5 transition-colors group">
                      <Facebook className="w-5 h-5 text-info" />
                      <span className="text-primary underline underline-offset-2 break-all">{businessProfile.facebook_link}</span>
                    </a>
                  )}
                  {businessProfile.instagram_link && (
                    <a href={businessProfile.instagram_link} target="_blank" rel="noopener noreferrer"
                      className="flex items-center gap-3 p-3 bg-surface2 rounded-md hover:bg-primary/5 transition-colors group">
                      <Instagram className="w-5 h-5 text-accent" />
                      <span className="text-primary underline underline-offset-2 break-all">{businessProfile.instagram_link}</span>
                    </a>
                  )}
                  {businessProfile.linkedin_link && (
                    <a href={businessProfile.linkedin_link} target="_blank" rel="noopener noreferrer"
                      className="flex items-center gap-3 p-3 bg-surface2 rounded-md hover:bg-primary/5 transition-colors group">
                      <Linkedin className="w-5 h-5 text-info" />
                      <span className="text-primary underline underline-offset-2 break-all">{businessProfile.linkedin_link}</span>
                    </a>
                  )}
                </div>
              </div>

              <div className="p-6 bg-surface rounded-lg ring-1 ring-line/10 text-left">
                <h4 className="font-display font-bold text-primary mb-4 flex items-center gap-2">
                  <Briefcase className="w-5 h-5 text-primary" />
                  Professional Details
                </h4>
                <div className="space-y-4 text-sm">
                  {businessProfile.business_type === 'salary' ? (
                    <>
                      <div className="flex items-start gap-3">
                        <Briefcase className="w-4 h-4 text-primary mt-1 flex-shrink-0" />
                        <div>
                          <span className="font-semibold text-ink block mb-1">Designation</span>
                          <p className="text-text">{businessProfile.designation || 'Not specified'}</p>
                        </div>
                      </div>
                      <div className="flex items-start gap-3">
                        <TrendingUp className="w-4 h-4 text-primary mt-1 flex-shrink-0" />
                        <div>
                          <span className="font-semibold text-ink block mb-1">Salary</span>
                          <p className="text-text">{businessProfile.salary || 'Not specified'}</p>
                        </div>
                      </div>
                    </>
                  ) : (
                    <>
                      <div className="flex items-start gap-3">
                        <TrendingUp className="w-4 h-4 text-primary mt-1 flex-shrink-0" />
                        <div>
                          <span className="font-semibold text-ink block mb-1">Source</span>
                          <p className="text-text">{businessProfile.source || 'Not specified'}</p>
                        </div>
                      </div>
                    </>
                  )}
                  <div className="flex items-start gap-3">
                    <Tag className="w-4 h-4 text-primary mt-1 flex-shrink-0" />
                    <div>
                      <span className="font-semibold text-ink block mb-1">Tags</span>
                      <div className="flex flex-wrap gap-1 mt-1">
                        {businessProfile.tags ? businessProfile.tags.split(',').map((tag, index) => (
                          <span key={index} className="bg-warning/10 text-warning px-3 py-1 rounded-lg text-xs font-medium ring-1 ring-warning/25 hover:bg-warning/15 transition-colors cursor-pointer">
                            #{tag.trim()}
                          </span>
                        )) : (
                          <span className="text-muted text-xs">Not specified</span>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-1 gap-8 mt-8">
            <div className='space-y-12 text-sm'>
              <div className="p-6 bg-surface rounded-lg ring-1 ring-line/10 text-left">
                <h4 className="font-display font-bold text-primary mb-4 flex items-center gap-2">
                  <MapPin className="w-5 h-5 text-primary" />
                  Location & Contact
                </h4>

                {branch.length > 0 ? (
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                    {branch.map((branch, index) => (
                      <div
                        key={index}
                        className="p-4 rounded-md ring-1 ring-primary/15 bg-primary/5 shadow-card"
                      >
                        <h5 className="font-display font-semibold text-primary mb-2">
                          {branch.branch_name || `Branch ${index + 1}`}
                        </h5>

                        <div className="space-y-3 text-sm text-left">
                          {/* Email */}
                          <div className="flex items-start gap-3">
                            <Mail className="w-4 h-4 text-primary mt-1 flex-shrink-0" />
                            <div>
                              <span className="font-semibold text-ink block mb-1">
                                Work Contact
                              </span>
                              <p className="text-text">
                                {branch.business_work_contact || "Not specified"}
                              </p>
                            </div>
                          </div>

                          {/* Address */}
                          <div className="flex items-start gap-3">
                            <MapPin className="w-4 h-4 text-primary mt-1 flex-shrink-0" />
                            <div>
                              <span className="font-semibold text-ink block mb-1">
                                Address
                              </span>
                              {branch.company_address ? (
                                <a
                                  href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(branch.company_address)}`}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="text-primary hover:text-primary-hi underline-offset-2 hover:underline"
                                >
                                  {branch.company_address}
                                </a>
                              ) : (
                                <p className="text-text">Not specified</p>
                              )}
                            </div>
                          </div>

                          {/* City */}
                          <div className="flex items-start gap-3">
                            <MapPin className="w-4 h-4 text-primary mt-1 flex-shrink-0" />
                            <div>
                              <span className="font-semibold text-ink block mb-1">
                                City
                              </span>
                              <p className="text-text">{branch.city || "Not specified"}</p>
                            </div>
                          </div>

                          {/* State */}
                          <div className="flex items-start gap-3">
                            <MapPin className="w-4 h-4 text-primary mt-1 flex-shrink-0" />
                            <div>
                              <span className="font-semibold text-ink block mb-1">
                                State
                              </span>
                              <p className="text-text">{branch.state || "Not specified"}</p>
                            </div>
                          </div>

                          {/* Zipcode */}
                          <div className="flex items-start gap-3">
                            <Tag className="w-4 h-4 text-primary mt-1 flex-shrink-0" />
                            <div>
                              <span className="font-semibold text-ink block mb-1">
                                Pincode
                              </span>
                              <p className="text-text">{branch.zip_code || "Not specified"}</p>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-text text-sm">No branches available.</p>
                )}
              </div>
          </div>
</div>

        </CollapsibleCard>

        {/* Business Images Card */}
        {(profileImageUrl || mediaUrls.length > 0) && (
          <CollapsibleCard
            title="Business Gallery"
            isExpanded={expandedSections.businessImages}
            onToggle={() => toggleSection('businessImages')}
            icon={Building2}
          >
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {/* Removed business profile image tile as requested */}
              {mediaUrls.map((url, idx) => {
                const isVideo = /\.(mp4|webm|ogg|avi|mov|wmv|flv|mkv|m4v|3gp)(\?|$)/i.test(url);
                return (
                  <div
                    key={idx}
                    className="group relative aspect-video bg-surface2 rounded-lg overflow-hidden shadow-card hover: transition-all duration-300 transform hover:-translate-y-2 cursor-pointer ring-1 ring-line/10"
                    onClick={() => {
                      setMediaPopupData({ url, isVideo, alt: `Business Media ${idx + 1}` });
                      setShowMediaPopup(true);
                    }}
                  >
                    {isVideo ? (
                      <video src={url} className="w-full h-full object-cover" muted />
                    ) : (
                      <img
                        src={url}
                        alt={`Business Media ${idx + 1}`}
                        className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                      />
                    )}
                    <div className="absolute inset-0 bg-gradient-to-t from-navy/70 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-end p-4">
                      <span className="text-white font-semibold">{isVideo ? 'Video' : `Image ${idx + 1}`}</span>
                    </div>
                  </div>
                );
              })}
            </div>
          </CollapsibleCard>
        )}

        {/* Family Information Card */}
        {resolvedFamily && (
          <CollapsibleCard
            title="Family Information"
            isExpanded={expandedSections.familyInfo}
            onToggle={() => toggleSection('familyInfo')}
            icon={Users}
          >
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 text-sm">
              <div className="space-y-4">
                <h4 className="font-display font-semibold text-primary border-b border-line/12 pb-2 text-left">Parents Information</h4>
                <div className="flex items-center gap-3">
                  <UserIcon className="w-4 h-4 text-primary flex-shrink-0" />
                  <div className="text-left">
                    <span className="font-semibold text-ink block">Father Name</span>
                    <p className="text-muted">{resolvedFamily?.father_name || 'Not available'}</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <Phone className="w-4 h-4 text-primary flex-shrink-0" />
                  <div className="text-left">
                    <span className="font-semibold text-ink block">Father Contact</span>
                    {resolvedFamily?.father_contact ? (
                      <a href={buildTelHref(resolvedFamily.father_contact)} className="text-primary hover:text-primary-hi underline-offset-2 hover:underline">{resolvedFamily.father_contact}</a>
                    ) : (
                      <p className="text-muted">Not available</p>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <UserIcon className="w-4 h-4 text-primary flex-shrink-0" />
                  <div className="text-left">
                    <span className="font-semibold text-ink block">Mother Name</span>
                    <p className="text-muted">{resolvedFamily?.mother_name || 'Not available'}</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <Phone className="w-4 h-4 text-primary flex-shrink-0" />
                  <div className="text-left">
                    <span className="font-semibold text-ink block">Mother Contact</span>
                    {resolvedFamily?.mother_contact ? (
                      <a href={buildTelHref(resolvedFamily.mother_contact)} className="text-primary hover:text-primary-hi underline-offset-2 hover:underline">{resolvedFamily.mother_contact}</a>
                    ) : (
                      <p className="text-muted">Not available</p>
                    )}
                  </div>
                </div>
              </div>
              <div className="space-y-4">
                <h4 className="font-display font-semibold text-primary border-b border-line/12 pb-2 text-left">Spouse Information</h4>
                <div className="flex items-center gap-3">
                  <Heart className="w-4 h-4 text-primary flex-shrink-0" />
                  <div className="text-left">
                    <span className="font-semibold text-ink block">Spouse Name</span>
                    <p className="text-muted">{resolvedFamily?.spouse_name || 'Not available'}</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <Phone className="w-4 h-4 text-primary flex-shrink-0" />
                  <div className="text-left">
                    <span className="font-semibold text-ink block">Spouse Contact</span>
                    {resolvedFamily?.spouse_contact ? (
                      <a href={buildTelHref(resolvedFamily.spouse_contact)} className="text-primary hover:text-primary-hi underline-offset-2 hover:underline">{resolvedFamily.spouse_contact}</a>
                    ) : (
                      <p className="text-muted">Not available</p>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <Clock className="w-4 h-4 text-primary flex-shrink-0" />
                  <div className="text-left">
                    <span className="font-semibold text-ink block">Anniversary Date</span>
                    <p className="text-muted">{resolvedFamily?.anniversary_date || 'Not available'}</p>
                  </div>
                </div>
              </div>
              <div className="space-y-4">
                <h4 className="font-display font-semibold text-primary border-b border-line/12 pb-2 text-left">Children Information</h4>
                <div className="flex items-center gap-3">
                  <Users className="w-4 h-4 text-primary flex-shrink-0" />
                  <div className="text-left">
                    <span className="font-semibold text-ink block">Number of Children</span>
                    <p className="text-muted">{resolvedFamily?.number_of_children || 'Not available'}</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <Users className="w-4 h-4 text-primary flex-shrink-0" />
                  <div className="text-left">
                    <span className="font-semibold text-ink block">Children Names</span>
                    <p className="text-muted">{formatChildrenNames(resolvedFamily?.children_names) || 'Not available'}</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <Phone className="w-4 h-4 text-primary flex-shrink-0" />
                  <div className="text-left">
                    <span className="font-semibold text-ink block">Emergency Contact</span>
                    <p className="text-muted">{resolvedFamily?.emergency_contact || 'Not available'}</p>
                  </div>
                </div>
              </div>
            </div>
            <div className="mt-8 pt-6 border-t border-line/12">
              <h4 className="font-display font-semibold text-primary mb-4 text-left">Family Address</h4>
              <div className="text-sm">
                <div className="flex items-center gap-3">
                  <MapPin className="w-4 h-4 text-primary flex-shrink-0" />
                  <div className="text-left">
                    <span className="font-semibold text-ink block">Family Address</span>
                    <p className="text-muted">{resolvedFamily?.address || 'Not available'}</p>
                  </div>
                </div>
              </div>
            </div>
          </CollapsibleCard>
        )}

        {/* Ratings & Reviews (static section) */}
        <Card padding="none" className="overflow-hidden">
          <div className="w-full px-6 py-3 flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="p-2 bg-primary/10 text-primary rounded-md">
                <Star className="w-6 h-6" />
              </div>
              <h3 className="font-display font-bold text-xl text-ink">Ratings & Reviews</h3>
            </div>
          </div>
          <div className="border-t border-line/12">
            <div className="p-4 bg-surface/40">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div className="space-y-6">
                  <div className="text-center p-6 bg-warning/10 rounded-lg ring-1 ring-warning/20">
                    <div className="glow-num text-5xl font-bold text-warning mb-3">
                      {averageRating > 0 ? averageRating.toFixed(1) : '0.0'}
                    </div>
                    <div className="flex justify-center">
                      <StarRating rating={Math.round(averageRating)} size="w-6 h-6" />
                    </div>
                    <p className="text-base font-semibold text-ink mt-2">Based on {ratings.length} reviews</p>
                    <p className="text-xs text-muted mt-1">Last updated 2 days ago</p>
                  </div>
                  <div className="space-y-3">
                    {ratingDistribution.map(({ rating, count, percentage }) => (
                      <div key={rating} className="flex items-center gap-4 p-2.5 bg-surface rounded-md ring-1 ring-line/10">
                        <div className="flex items-center gap-2 min-w-0">
                          <span className="text-sm font-medium text-text w-3 text-right">{rating}</span>
                          <Star className="w-4 h-4 fill-warning text-warning flex-shrink-0" />
                        </div>
                        <div className="flex-1 bg-line/15 rounded-full h-2.5 overflow-hidden">
                          <div
                            className="bg-warning h-2.5 rounded-full transition-all duration-500 ease-out"
                            style={{ width: `${percentage}%` }}
                          ></div>
                        </div>
                        <span className="text-sm font-medium text-text w-8 text-right">
                          {count}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
                <div className="flex flex-col justify-center items-center space-y-4 p-6 bg-warning/10 rounded-lg ring-1 ring-warning/20">
                  <div className="w-16 h-16 bg-warning rounded-lg flex items-center justify-center">
                    <Star className="w-8 h-8 text-white fill-white" />
                  </div>
                  <div className="text-center">
                    <p className="font-display font-bold text-xl text-ink">Referral Points: {resolvedMember?.reward_points || 0}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </Card>

        {/* Customer Reviews (static section) */}
        <Card padding="none" className="overflow-hidden">
          <div className="w-full px-6 py-5 flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="p-2 bg-primary/10 text-primary rounded-md">
                <Mail className="w-6 h-6" />
              </div>
              <h3 className="font-display font-bold text-xl text-ink">{`Customer Reviews (${ratings.length})`}</h3>
            </div>
          </div>
          <div className="border-t border-line/12">
            <div className="p-6 bg-surface/40">
              {ratingsLoading ? (
                <Loader label="Loading reviews..." />
              ) : ratingsError ? (
                <div className="text-center py-12">
                  <p className="text-danger font-medium mb-4">{ratingsError}</p>
                  <Button
                    variant="primary"
                    onClick={() => setRatingsReloadKey((k) => k + 1)}
                  >
                    Retry
                  </Button>
                </div>
              ) : ratings.length === 0 ? (
                <EmptyState
                  icon={<Star className="w-7 h-7" />}
                  title="No reviews yet"
                  description="Be the first to review this business!"
                  action={
                    <Button onClick={() => navigate(`/review/${businessProfile.id}`)}>
                      Write a Review
                    </Button>
                  }
                />
              ) : (
                <>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 mb-8 sm:mb-10">
                    {(showAllReviews ? ratings : ratings.slice(0, 3)).map((review, index) => {
                      const reviewerName = review.ratedBy
                        ? `${review.ratedBy.first_name || ''} ${review.ratedBy.last_name || ''}`.trim()
                        : review.member_name || 'Anonymous';

                      const reviewerInitials = reviewerName !== 'Anonymous'
                        ? reviewerName.split(' ').map(n => n.charAt(0)).join('').toUpperCase()
                        : 'AN';

                      const avatarUrl = review.ratedBy?.profile_image
                        ? (review.ratedBy.profile_image.startsWith('https')
                          ? review.ratedBy.profile_image
                          : `${baseurl}/${review.ratedBy.profile_image}`)
                        : review.member_profile_image
                          ? (review.member_profile_image.startsWith('https')
                            ? review.member_profile_image
                            : `${baseurl}/${review.member_profile_image}`)
                          : '';

                      return (
                        <div key={review.rid || index} className="p-4 sm:p-6 bg-surface rounded-lg ring-1 ring-line/10 shadow-card h-48 sm:h-56 flex flex-col text-left">
                          <div className="flex items-start gap-3 sm:gap-4">
                            <Avatar src={avatarUrl} name={reviewerName !== 'Anonymous' ? reviewerName : reviewerInitials} size="md" />
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center justify-between gap-2">
                                <div className="font-semibold text-ink truncate text-sm sm:text-base">{reviewerName}</div>
                                <div className="text-xs sm:text-sm text-muted whitespace-nowrap">{formatRelativeTime(review.createdAt)}</div>
                              </div>
                              <div className="mt-1 flex items-center gap-1 text-warning">
                                <StarRating rating={review.rating} size="w-4 h-4" />
                              </div>
                              <div className="relative mt-2 sm:mt-3 text-text leading-relaxed break-words text-sm sm:text-base">
                                <div className="max-h-16 overflow-hidden whitespace-pre-wrap">{review.message || review.review_text || 'No review message provided.'}</div>
                                {(String(review.message || review.review_text || '').length > 120) && (
                                  <div className="pointer-events-none absolute inset-x-0 bottom-0 h-8 bg-gradient-to-t from-surface to-transparent"></div>
                                )}
                              </div>
                            </div>
                          </div>
                          <div className="mt-auto pt-2 sm:pt-3 text-right">
                            <button
                              className="text-xs sm:text-sm font-semibold text-primary hover:text-primary-hi"
                              onClick={() => { setSelectedReview(review); setIsReviewPopupOpen(true); }}
                            >
                              Read full review
                            </button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                  <div className="flex flex-col sm:flex-row gap-4 justify-center">
                    {ratings.length > 3 && (
                      <Button
                        variant="ghost"
                        onClick={() => setShowAllReviews(prev => !prev)}
                      >
                        {showAllReviews ? 'Show Less' : `View All ${ratings.length} Reviews`}
                      </Button>
                    )}
                    <Button
                      variant="primary"
                      onClick={() => navigate(`/review/${businessProfile.id}`)}
                    >
                      Write a Review
                    </Button>
                  </div>
                </>
              )}
            </div>
          </div>
        </Card>
      </div>

      {isReviewPopupOpen && (
        <ReviewPopup review={selectedReview} onClose={() => { setIsReviewPopupOpen(false); setSelectedReview(null); }} />
      )}

      {showMediaPopup && (
        <MediaPopup
          url={mediaPopupData.url}
          isVideo={mediaPopupData.isVideo}
          altText={mediaPopupData.alt}
          onClose={() => setShowMediaPopup(false)}
        />
      )}

      <div className="hidden md:block">
        <Footer />
      </div>
      <div className="md:hidden">
        <MobileFooter />
      </div>
    </div>
  );
};

export default BusinessListing;
